/**
 * Safe formatting utilities for handling potentially invalid data
 * Prevents NaN display issues by validating inputs before formatting
 */

/**
 * Checks if a date string represents a valid, non-zero date
 * Handles Go's zero time '0001-01-01T00:00:00Z' and other edge cases
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  // Check for Go's zero time
  if (dateString.startsWith('0001-01-01')) {
    return false;
  }

  const date = new Date(dateString);
  
  // Check if date is valid and not NaN
  if (isNaN(date.getTime())) {
    return false;
  }

  // Check if date is not Unix epoch (often indicates empty/default value)
  if (date.getTime() === 0) {
    return false;
  }

  return true;
};

/**
 * Safely formats a date string to Vietnamese locale format
 * Returns fallback text for invalid dates instead of NaN
 */
export const formatDateVietnamese = (
  dateString: string | null | undefined,
  fallback: string = '--'
): string => {
  if (!isValidDate(dateString)) {
    return fallback;
  }

  try {
    const date = new Date(dateString!);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${hours}:${minutes} ${day}/${month}/${year}`;
  } catch (error) {
    console.warn('Error formatting date:', dateString, error);
    return fallback;
  }
};

/**
 * Safely formats a date string using Intl.DateTimeFormat
 * Returns fallback text for invalid dates instead of NaN
 */
export const formatDate = (
  dateString: string | null | undefined,
  fallback: string = '--'
): string => {
  if (!isValidDate(dateString)) {
    return fallback;
  }

  try {
    const date = new Date(dateString!);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.warn('Error formatting date:', dateString, error);
    return fallback;
  }
};

/**
 * Checks if a value is a valid number for currency formatting
 */
export const isValidNumber = (value: any): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  const num = Number(value);
  return !isNaN(num) && isFinite(num);
};

// Import standardized formatting functions
import { formatCurrency as standardFormatCurrency, formatNumber as standardFormatNumber } from './number-format';

/**
 * Safely formats a number as Vietnamese currency with standardized formatting
 * Returns fallback text for invalid numbers instead of NaN
 */
export const formatCurrency = (
  amount: any,
  fallback: string = '0 ₫'
): string => {
  if (!isValidNumber(amount)) {
    return fallback;
  }

  try {
    const num = Number(amount);
    return standardFormatCurrency(num);
  } catch (error) {
    console.warn('Error formatting currency:', amount, error);
    return fallback;
  }
};

/**
 * Safely formats a number with Vietnamese locale and standardized formatting
 * Returns fallback text for invalid numbers instead of NaN
 */
export const formatNumber = (
  value: any,
  fallback: string = '0'
): string => {
  if (!isValidNumber(value)) {
    return fallback;
  }

  try {
    const num = Number(value);
    return standardFormatNumber(num);
  } catch (error) {
    console.warn('Error formatting number:', value, error);
    return fallback;
  }
};

/**
 * Safely gets a string value with fallback
 * Handles null, undefined, and empty strings
 */
export const safeString = (
  value: any,
  fallback: string = '--'
): string => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const str = String(value).trim();
  return str === '' ? fallback : str;
};

/**
 * Safely formats service unit with fallback
 * Returns 'N/A' for empty or invalid units
 */
export const safeUnit = (unit: string | undefined | null): string => {
  if (!unit || unit.trim() === '') {
    return 'N/A';
  }
  return String(unit);
};
